import { useEffect, useRef, useState } from 'react';
import { businessDate, fromJalali, toJalali, systemClock } from '@sakhtar/calendar';
import { Icon, V2Button } from '@sakhtar/design-system/react';
import { apiPatch, apiPost, ApiError } from '../../lib/apiClient.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { useIsDesktopAuth } from '../auth/AuthHeroPanel.js';
import { PresetFace } from '../../lib/avatars.js';
import {
  ALLOWED_AVATAR_MIME_TYPES,
  AVATAR_MAX_BYTES,
  type AvatarPresetId,
  type BrandColor,
  type OwnProfilePatchBody,
  type OwnProfileResponse,
  type SidebarSkin,
  type ThemeMode,
} from '../../lib/types/membership.js';
import {
  AVATAR_PRESET_DEFS,
  BRAND_COLOR_DEFS,
  JALALI_MONTH_NAMES,
  SIDEBAR_SKIN_DEFS,
  THEME_MODE_DEFS,
  avatarGradient,
  birthYearOptions,
} from './firstLoginProfileConstants.js';

type AvatarKind = 'none' | 'preset' | 'photo';

/** What the wizard just saved — lets the shell show it before `me` is refetched. */
export interface CompletedProfile {
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly avatarPreset: AvatarPresetId | null;
  readonly avatarPhotoDataUrl: string | null;
  readonly brandColor: BrandColor | null;
  readonly sidebarSkin: SidebarSkin | null;
}

type Step = 1 | 2 | 3 | 4 | 5;

const STEP_TITLES: Record<Step, [string, string]> = {
  1: ['تکمیل پروفایل', 'نخستین ورود شما به فضای کاری'],
  2: ['اطلاعات شخصی', 'هویت شما در سازمان'],
  3: ['عکس پروفایل', 'آپلود عکس یا انتخاب آواتار'],
  4: ['ظاهر حساب شما', 'رنگ، پوسته و حالت نمایش'],
  5: ['آماده است', 'خلاصهٔ پروفایل شما'],
};

function parseDataUrl(dataUrl: string): { mimeType: string; base64: string } | null {
  const match = /^data:([^;]+);base64,(.+)$/.exec(dataUrl);
  if (!match) return null;
  return { mimeType: match[1]!, base64: match[2]! };
}

function faDigits(value: string | number): string {
  return String(value).replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]!);
}

/**
 * First Login Profile — a real, backend-wired profile-completion wizard
 * shown once per user (`profileCompletedAt === null`) after login, before
 * the app becomes usable. Desktop renders as a blocking modal over the
 * current page; mobile renders full-screen. Both share the same step
 * content — only the surrounding chrome differs, matching the design's own
 * D1-D5/M1-M5 structure (identical content, different frame).
 *
 * `admin`-gated fields in the design (a client-toggleable preview role) are
 * real here: `profile.canEditLockedFields` comes from the server
 * (`isFullTenantAdmin`), never a client toggle. The design's fake
 * dashboard-mockup background, role/step-jump preview toolbar and reset
 * button are design-canvas-only scaffolding and have no real counterpart.
 *
 * UI_REFERENCE_SOURCE: Sakhtar First Login Profile.dc.html (read directly
 *   via DesignSync). status: PARTIAL_REAL — step structure, field set and
 *   locked-field framing follow the design; the preview toolbar and
 *   background dashboard mockup are omitted (design-tool-only).
 */
export function FirstLoginProfileFlow({
  slug,
  profile,
  onDone,
  onAppearancePreview,
  onThemeModePreview,
}: {
  readonly slug: string;
  readonly profile: OwnProfileResponse;
  readonly onDone: (completed: CompletedProfile) => void;
  /**
   * Live preview hook for step 4 ("ظاهر حساب شما"): AuthenticatedShell
   * applies `brandColor`/`sidebarSkin` to its own `data-brand`/`data-sidebar`
   * wrapper only from the saved profile (`me.data`), so picking a swatch
   * here previously changed nothing until the PATCH round-trip completed
   * and `/me` was refetched. Called on every change so the real sidebar/
   * topbar behind this modal repaints immediately, before saving.
   */
  readonly onAppearancePreview?: (appearance: {
    readonly brandColor: BrandColor;
    readonly sidebarSkin: SidebarSkin;
  }) => void;
  /** Same idea as `onAppearancePreview`, for `themeMode` — reported live as
   * the one field of this step that didn't actually apply anywhere outside
   * this modal's own local `data-theme` (below) even after confirming.
   * AuthenticatedShell wires this to the real global ThemeProvider's
   * `setMode`, the same one the topbar's ThemeToggle uses. */
  readonly onThemeModePreview?: (mode: ThemeMode) => void;
}) {
  const isDesktop = useIsDesktopAuth();
  const base = tenantBasePath(slug);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [step, setStep] = useState<Step>(1);
  const [first, setFirst] = useState(profile.firstName ?? '');
  const [last, setLast] = useState(profile.lastName ?? '');
  const [email, setEmail] = useState(profile.email ?? '');

  const initialJalali = (() => {
    if (!profile.birthdate) return null;
    try {
      return toJalali(businessDate(profile.birthdate));
    } catch {
      return null;
    }
  })();
  const [bd, setBd] = useState(initialJalali ? String(initialJalali.jd) : '');
  const [bm, setBm] = useState(initialJalali ? String(initialJalali.jm) : '');
  const [by, setBy] = useState(initialJalali ? String(initialJalali.jy) : '');

  const [avatarKind, setAvatarKind] = useState<AvatarKind>(
    profile.avatarPreset ? 'preset' : profile.avatarPhotoDataUrl ? 'photo' : 'none',
  );
  const [avatarPreset, setAvatarPreset] = useState<AvatarPresetId | null>(profile.avatarPreset);
  const [avatarPhotoDataUrl, setAvatarPhotoDataUrl] = useState<string | null>(
    profile.avatarPhotoDataUrl,
  );

  const [themeMode, setThemeMode] = useState<ThemeMode>(profile.themeMode ?? 'light');
  const [brandColor, setBrandColor] = useState<BrandColor>(profile.brandColor ?? 'indigo');
  const [sidebarSkin, setSidebarSkin] = useState<SidebarSkin>(profile.sidebarSkin ?? 'brand');

  useEffect(() => {
    onAppearancePreview?.({ brandColor, sidebarSkin });
  }, [brandColor, sidebarSkin, onAppearancePreview]);

  useEffect(() => {
    onThemeModePreview?.(themeMode);
  }, [themeMode, onThemeModePreview]);

  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [avatarError, setAvatarError] = useState<string | null>(null);

  const locked = !profile.canEditLockedFields;
  const nameOk = first.trim() !== '' && last.trim() !== '';

  function birthdateIso(): string | null {
    if (!bd || !by || !bm) return null;
    try {
      return fromJalali({ jy: Number(by), jm: Number(bm), jd: Number(bd) });
    } catch {
      return null;
    }
  }
  const birthOk = birthdateIso() !== null;
  // locked (not a full admin): name is read-only/preset — never gates advance.
  // !locked (full admin): name is editable and required, same as birthdate.
  const stepBlocked = step === 2 && !(birthOk && (locked || nameOk));

  const todayJ = toJalali(systemClock.today());
  const years = birthYearOptions(todayJ.jy);

  function avatarPatchValue(): NonNullable<OwnProfilePatchBody['avatar']> {
    if (avatarKind === 'preset' && avatarPreset) return { kind: 'preset', preset: avatarPreset };
    if (avatarKind === 'photo' && avatarPhotoDataUrl) {
      const parsed = parseDataUrl(avatarPhotoDataUrl);
      if (parsed && (ALLOWED_AVATAR_MIME_TYPES as readonly string[]).includes(parsed.mimeType)) {
        return {
          kind: 'photo',
          mimeType: parsed.mimeType as (typeof ALLOWED_AVATAR_MIME_TYPES)[number],
          base64: parsed.base64,
        };
      }
    }
    return { kind: 'none' };
  }

  function patchForStep(from: Step): OwnProfilePatchBody | null {
    if (from === 1) return null;
    if (from === 2) {
      const iso = birthdateIso();
      return {
        ...(locked ? {} : { firstName: first.trim(), lastName: last.trim() }),
        email: email.trim() === '' ? null : email.trim(),
        birthdate: iso,
      };
    }
    if (from === 3) return { avatar: avatarPatchValue() };
    if (from === 4) return { themeMode, brandColor, sidebarSkin };
    return null;
  }

  async function advance(target: Step) {
    if (step === 2 && stepBlocked) {
      setTouched(true);
      return;
    }
    const patch = patchForStep(step);
    if (patch) {
      setSaving(true);
      setError(null);
      try {
        await apiPatch<OwnProfileResponse>(`${base}/api/v1/me`, patch);
      } catch (err) {
        setSaving(false);
        setError(
          err instanceof ApiError ? 'ذخیره‌سازی انجام نشد. دوباره تلاش کنید.' : 'خطای شبکه.',
        );
        return;
      }
      setSaving(false);
    }
    setTouched(false);
    setStep(target);
  }

  async function finish() {
    setSaving(true);
    setError(null);
    try {
      await apiPost(`${base}/api/v1/me/complete-profile`);
    } catch {
      setSaving(false);
      setError('ثبت پروفایل انجام نشد. دوباره تلاش کنید.');
      return;
    }
    setSaving(false);
    onDone({
      firstName: first.trim() || null,
      lastName: last.trim() || null,
      avatarPreset: avatarKind === 'preset' ? avatarPreset : null,
      avatarPhotoDataUrl: avatarKind === 'photo' ? avatarPhotoDataUrl : null,
      brandColor,
      sidebarSkin,
    });
  }

  function onPickFile(file: File | undefined) {
    setAvatarError(null);
    if (!file) return;
    if (!(ALLOWED_AVATAR_MIME_TYPES as readonly string[]).includes(file.type)) {
      setAvatarError('فقط تصویر PNG، JPEG یا WEBP پذیرفته می‌شود.');
      return;
    }
    if (file.size > AVATAR_MAX_BYTES) {
      setAvatarError('حجم تصویر باید کمتر از ۲ مگابایت باشد.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setAvatarPhotoDataUrl(String(reader.result));
      setAvatarKind('photo');
      setAvatarPreset(null);
    };
    reader.readAsDataURL(file);
  }

  const initials = `${first.slice(0, 1)}${last.slice(0, 1)}`;
  const activePresetDef = avatarPreset
    ? AVATAR_PRESET_DEFS.find((a) => a.id === avatarPreset)
    : undefined;
  const bigBg =
    avatarKind === 'photo' && avatarPhotoDataUrl
      ? 'var(--sk-bg-canvas)'
      : activePresetDef
        ? avatarGradient(activePresetDef)
        : 'linear-gradient(158deg, var(--sk-brand-400), var(--sk-brand-600))';

  const cta =
    step === 1 ? 'شروع' : step === 4 ? 'ذخیره و ادامه' : step === 5 ? 'ورود به فضای کاری' : 'بعدی';
  const stepLabel =
    step === 1 ? 'پیش از شروع' : step === 5 ? 'پایان' : `مرحله ${faDigits(step - 1)} از ۳`;
  const pct = Math.round(((step - 1) / 4) * 100);

  const summaryRows: { label: string; value: string; onEdit: Step }[] = [
    { label: 'نام و نام خانوادگی', value: `${first} ${last}`.trim() || '—', onEdit: 2 },
    { label: 'ایمیل', value: email || '—', onEdit: 2 },
    {
      label: 'تاریخ تولد',
      value: birthOk
        ? `${faDigits(bd)} ${JALALI_MONTH_NAMES[Number(bm) - 1]} ${faDigits(by)}`
        : 'ثبت نشده',
      onEdit: 2,
    },
    {
      label: 'عکس پروفایل',
      value:
        avatarKind === 'photo'
          ? 'عکس آپلود شده'
          : avatarKind === 'preset'
            ? 'آواتار انتخابی'
            : 'حرف اول نام',
      onEdit: 3,
    },
    {
      label: 'حالت نمایش',
      value: THEME_MODE_DEFS.find((t) => t.id === themeMode)?.label ?? '',
      onEdit: 4,
    },
    {
      label: 'رنگ اصلی',
      value: BRAND_COLOR_DEFS.find((b) => b.id === brandColor)?.label ?? '',
      onEdit: 4,
    },
  ];

  function StepBody() {
    if (step === 1) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--sk-fg)' }}>
              خوش آمدید{first ? `، ${first}` : ''}
            </h2>
            <p
              style={{
                margin: 0,
                fontSize: 14,
                lineHeight: 2,
                color: 'var(--sk-fg-muted)',
                maxWidth: 560,
              }}
            >
              پیش از شروع، پروفایل شخصی خود را تکمیل کنید. کمتر از یک دقیقه طول می‌کشد.
            </p>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isDesktop ? 'repeat(3, 1fr)' : '1fr',
              gap: 12,
            }}
          >
            {[
              { icon: 'user' as const, title: 'اطلاعات شخصی', body: 'تأیید نام و ثبت تاریخ تولد' },
              {
                icon: 'layers' as const,
                title: 'عکس پروفایل',
                body: 'آپلود عکس یا انتخاب یکی از آواتارها',
              },
              {
                icon: 'settings' as const,
                title: 'ظاهر حساب',
                body: 'رنگ اصلی، پوستهٔ ساید‌بار و حالت روشن یا تاریک',
              },
            ].map((item) => (
              <div
                key={item.title}
                style={{
                  padding: 14,
                  borderRadius: 'var(--sk-r-lg)',
                  border: '1px solid var(--sk-border)',
                  background: 'var(--sk-bg-canvas)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 9,
                }}
              >
                <span
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 10,
                    background: 'color-mix(in oklch, var(--sk-brand-500), transparent 88%)',
                    color: 'var(--sk-brand-600)',
                    display: 'grid',
                    placeContent: 'center',
                  }}
                >
                  <Icon name={item.icon} size="sm" />
                </span>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--sk-fg)' }}>
                  {item.title}
                </span>
                <span style={{ fontSize: 12, lineHeight: 1.85, color: 'var(--sk-fg-muted)' }}>
                  {item.body}
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (step === 2) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {locked ? (
            <div
              style={{
                display: 'flex',
                gap: 10,
                alignItems: 'flex-start',
                padding: '12px 14px',
                borderRadius: 'var(--sk-r-md)',
                background: 'var(--sk-info-soft)',
              }}
            >
              <span style={{ color: '#2f6fa8', display: 'flex', paddingTop: 2 }}>
                <Icon name="shield" size="sm" />
              </span>
              <span style={{ fontSize: 12.5, lineHeight: 1.9, color: 'var(--sk-fg-muted)' }}>
                نام و نام خانوادگی شما توسط ادمین سازمان ثبت شده و تنها او می‌تواند آن را تغییر دهد.
                برای اصلاح، با ادمین سازمان تماس بگیرید.
              </span>
            </div>
          ) : null}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isDesktop ? '1fr 1fr' : '1fr',
              gap: 14,
            }}
          >
            <div className="sk-field">
              <label>
                نام<span style={{ color: 'var(--sk-danger)' }}> *</span>
              </label>
              <input
                className="sk-input"
                value={first}
                onChange={(e) => setFirst(e.target.value)}
                readOnly={locked}
                style={{
                  background: locked ? 'var(--sk-bg-canvas)' : undefined,
                  borderColor:
                    touched && !locked && first.trim() === '' ? 'var(--sk-danger)' : undefined,
                }}
              />
            </div>
            <div className="sk-field">
              <label>
                نام خانوادگی<span style={{ color: 'var(--sk-danger)' }}> *</span>
              </label>
              <input
                className="sk-input"
                value={last}
                onChange={(e) => setLast(e.target.value)}
                readOnly={locked}
                style={{
                  background: locked ? 'var(--sk-bg-canvas)' : undefined,
                  borderColor:
                    touched && !locked && last.trim() === '' ? 'var(--sk-danger)' : undefined,
                }}
              />
              {touched && !locked && !nameOk ? (
                <span style={{ fontSize: 11, color: 'var(--sk-danger)' }}>
                  نام و نام خانوادگی الزامی است.
                </span>
              ) : null}
            </div>
            <div className="sk-field">
              <label>شماره تلفن همراه</label>
              <input
                className="sk-input"
                value={profile.mobile ?? ''}
                readOnly
                dir="ltr"
                style={{ background: 'var(--sk-bg-canvas)', direction: 'ltr', textAlign: 'left' }}
              />
              <span style={{ fontSize: 11, color: 'var(--sk-fg-subtle)' }}>
                شناسهٔ ورود شماست — همیشه از طریق پشتیبانی تغییر می‌کند.
              </span>
            </div>
            <div className="sk-field">
              <label>ایمیل</label>
              <input
                className="sk-input"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="اختیاری"
                dir="ltr"
                style={{ direction: 'ltr', textAlign: 'left' }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--sk-fg)' }}>
                تاریخ تولد
              </span>
              <span style={{ color: 'var(--sk-danger)', fontSize: 13 }}>*</span>
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1.3fr 1fr',
                gap: 10,
                maxWidth: 460,
              }}
            >
              <select className="sk-select" value={bd} onChange={(e) => setBd(e.target.value)}>
                <option value="">روز</option>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={d}>
                    {faDigits(d)}
                  </option>
                ))}
              </select>
              <select className="sk-select" value={bm} onChange={(e) => setBm(e.target.value)}>
                <option value="">ماه</option>
                {JALALI_MONTH_NAMES.map((m, i) => (
                  <option key={m} value={i + 1}>
                    {m}
                  </option>
                ))}
              </select>
              <select className="sk-select" value={by} onChange={(e) => setBy(e.target.value)}>
                <option value="">سال</option>
                {years.map((y) => (
                  <option key={y} value={y}>
                    {faDigits(y)}
                  </option>
                ))}
              </select>
            </div>
            {touched && !birthOk ? (
              <span style={{ fontSize: 12, color: 'var(--sk-danger)' }}>
                برای ادامه، تاریخ تولد را کامل و درست انتخاب کنید.
              </span>
            ) : null}
          </div>
        </div>
      );
    }

    if (step === 3) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div style={{ display: 'flex', gap: 22, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}
            >
              <span
                style={{
                  position: 'relative',
                  width: 96,
                  height: 96,
                  borderRadius: '50%',
                  overflow: 'hidden',
                  background: bigBg,
                  border: '1px solid var(--sk-border)',
                  color: '#fff',
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: 28,
                  fontWeight: 600,
                }}
              >
                {avatarKind === 'photo' && avatarPhotoDataUrl ? (
                  <img
                    src={avatarPhotoDataUrl}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : activePresetDef ? (
                  <PresetFace def={activePresetDef} />
                ) : (
                  initials || <Icon name="user" size="lg" />
                )}
              </span>
              <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>
                {avatarKind === 'photo'
                  ? 'عکس آپلودی شما'
                  : avatarKind === 'preset'
                    ? 'آواتار انتخاب‌شده'
                    : 'بدون عکس — حرف اول نام نمایش داده می‌شود'}
              </span>
            </div>
            <div
              style={{ flex: 1, minWidth: 220, display: 'flex', flexDirection: 'column', gap: 10 }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                style={{ display: 'none' }}
                onChange={(e) => onPickFile(e.target.files?.[0])}
              />
              <V2Button
                variant="secondary"
                icon="upload"
                onClick={() => fileInputRef.current?.click()}
              >
                آپلود عکس
              </V2Button>
              {avatarKind !== 'none' ? (
                <V2Button
                  variant="ghost"
                  onClick={() => {
                    setAvatarKind('none');
                    setAvatarPreset(null);
                    setAvatarPhotoDataUrl(null);
                  }}
                >
                  حذف عکس
                </V2Button>
              ) : null}
              {avatarError ? (
                <span style={{ fontSize: 12, color: 'var(--sk-danger)' }}>{avatarError}</span>
              ) : null}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--sk-fg)' }}>
              یا یکی از آواتارها را انتخاب کنید
            </span>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isDesktop ? 'repeat(10, 1fr)' : 'repeat(5, 1fr)',
                gap: 10,
              }}
            >
              {AVATAR_PRESET_DEFS.map((a, index) => {
                const selected = avatarKind === 'preset' && avatarPreset === a.id;
                return (
                  <button
                    key={a.id}
                    type="button"
                    aria-label={`آواتار ${faDigits(index + 1)}`}
                    aria-pressed={selected}
                    onClick={() => {
                      setAvatarKind('preset');
                      setAvatarPreset(a.id);
                      setAvatarPhotoDataUrl(null);
                    }}
                    style={{
                      position: 'relative',
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      padding: 0,
                      overflow: 'hidden',
                      border: '1px solid transparent',
                      boxShadow: selected
                        ? '0 0 0 2px var(--sk-bg-elev), 0 0 0 4px var(--sk-brand-500)'
                        : 'none',
                      background: avatarGradient(a),
                      color: '#fff',
                      display: 'grid',
                      placeContent: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    <PresetFace def={a} iconSize="sm" />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      );
    }

    if (step === 4) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--sk-fg)' }}>حالت نمایش</span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {THEME_MODE_DEFS.map((t) => {
                const selected = themeMode === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setThemeMode(t.id)}
                    style={{
                      textAlign: 'start',
                      cursor: 'pointer',
                      minHeight: 54,
                      padding: '11px 13px',
                      borderRadius: 'var(--sk-r-lg)',
                      border: `1.5px solid ${selected ? 'var(--sk-brand-500)' : 'var(--sk-border)'}`,
                      background: selected
                        ? 'color-mix(in oklch, var(--sk-brand-500), transparent 94%)'
                        : 'var(--sk-bg-canvas)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                    }}
                  >
                    <span
                      style={{
                        color: selected ? 'var(--sk-brand-600)' : 'var(--sk-fg-muted)',
                        display: 'flex',
                      }}
                    >
                      <Icon name={t.icon} size="sm" />
                    </span>
                    <span style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--sk-fg)' }}>
                        {t.label}
                      </span>
                      <span style={{ fontSize: 11, color: 'var(--sk-fg-muted)' }}>{t.hint}</span>
                    </span>
                    {selected ? (
                      <span style={{ color: 'var(--sk-brand-600)', display: 'flex' }}>
                        <Icon name="check" size="sm" />
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--sk-fg)' }}>
              رنگ اصلی حساب
            </span>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              {BRAND_COLOR_DEFS.map((b) => {
                const selected = brandColor === b.id;
                return (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => setBrandColor(b.id)}
                    style={{
                      cursor: 'pointer',
                      minHeight: 80,
                      padding: 10,
                      borderRadius: 'var(--sk-r-lg)',
                      border: `1.5px solid ${selected ? 'var(--sk-brand-500)' : 'var(--sk-border)'}`,
                      background: selected
                        ? 'color-mix(in oklch, var(--sk-brand-500), transparent 94%)'
                        : 'var(--sk-bg-canvas)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 7,
                    }}
                  >
                    <span
                      style={{
                        width: 30,
                        height: 30,
                        borderRadius: '50%',
                        background: b.color,
                        display: 'grid',
                        placeContent: 'center',
                        color: '#fff',
                      }}
                    >
                      {selected ? <Icon name="check" size="sm" /> : null}
                    </span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--sk-fg)' }}>
                      {b.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--sk-fg)' }}>
              پوستهٔ ساید‌بار و منوی اپ
            </span>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {SIDEBAR_SKIN_DEFS.map((p) => {
                const selected = sidebarSkin === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setSidebarSkin(p.id)}
                    style={{
                      cursor: 'pointer',
                      padding: 10,
                      borderRadius: 'var(--sk-r-lg)',
                      border: `1.5px solid ${selected ? 'var(--sk-brand-500)' : 'var(--sk-border)'}`,
                      background: selected
                        ? 'color-mix(in oklch, var(--sk-brand-500), transparent 94%)'
                        : 'var(--sk-bg-canvas)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                    }}
                  >
                    <span
                      style={{
                        height: 38,
                        borderRadius: 'var(--sk-r-sm)',
                        overflow: 'hidden',
                        display: 'flex',
                        border: '1px solid var(--sk-border)',
                      }}
                    >
                      <span style={{ flex: '0 0 34%', background: p.bg }} />
                      <span style={{ flex: 1, background: 'var(--sk-bg-canvas)' }} />
                    </span>
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 12,
                        fontWeight: 600,
                        color: 'var(--sk-fg)',
                      }}
                    >
                      {p.label}
                      {selected ? (
                        <span style={{ marginInlineStart: 'auto', color: 'var(--sk-brand-600)' }}>
                          <Icon name="check" size="sm" />
                        </span>
                      ) : null}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          <span style={{ fontSize: 11.5, lineHeight: 1.85, color: 'var(--sk-fg-muted)' }}>
            این تنظیمات مخصوص حساب شماست.
          </span>
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 12,
            paddingTop: 8,
          }}
        >
          <span
            style={{
              position: 'relative',
              width: 84,
              height: 84,
              borderRadius: '50%',
              overflow: 'hidden',
              background: bigBg,
              border: '1px solid var(--sk-border)',
              color: '#fff',
              display: 'grid',
              placeItems: 'center',
              fontSize: 26,
              fontWeight: 600,
            }}
          >
            {avatarKind === 'photo' && avatarPhotoDataUrl ? (
              <img
                src={avatarPhotoDataUrl}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : activePresetDef ? (
              <PresetFace def={activePresetDef} />
            ) : (
              initials || <Icon name="user" size="lg" />
            )}
          </span>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--sk-fg)' }}>
            پروفایل شما تکمیل شد
          </h2>
          <span style={{ fontSize: 12.5, color: 'var(--sk-fg-muted)' }}>
            حالا می‌توانید کار با اپ را شروع کنید.
          </span>
        </div>
        <div
          style={{
            border: '1px solid var(--sk-border)',
            borderRadius: 'var(--sk-r-lg)',
            overflow: 'hidden',
          }}
        >
          {summaryRows.map((row) => (
            <button
              key={row.label}
              type="button"
              onClick={() => setStep(row.onEdit)}
              style={{
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                gap: 10,
                padding: '12px 14px',
                borderBottom: '1px solid var(--sk-divider)',
                background: 'var(--sk-bg-elev)',
                cursor: 'pointer',
                textAlign: 'start',
              }}
            >
              <span
                style={{
                  fontSize: 11.5,
                  color: 'var(--sk-fg-muted)',
                  width: 110,
                  flex: '0 0 auto',
                }}
              >
                {row.label}
              </span>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--sk-fg)', flex: 1 }}>
                {row.value}
              </span>
              <Icon name="chevronLeft" size="sm" />
            </button>
          ))}
        </div>
      </div>
    );
  }

  const header = (
    <div
      style={{
        flex: '0 0 auto',
        padding: isDesktop ? '20px 26px 16px' : '18px 18px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        borderBottom: '1px solid var(--sk-divider)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span
          style={{
            width: 36,
            height: 36,
            borderRadius: 11,
            background: 'linear-gradient(160deg, var(--sk-brand-400), var(--sk-brand-600))',
            color: '#fff',
            display: 'grid',
            placeContent: 'center',
            flex: '0 0 auto',
          }}
        >
          <Icon name="user" size="sm" />
        </span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--sk-fg)' }}>
            {STEP_TITLES[step][0]}
          </span>
          <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>{STEP_TITLES[step][1]}</span>
        </div>
        <span className="sk-badge sk-badge--neutral">{stepLabel}</span>
      </div>
      <div className="sk-prog">
        <div className="sk-prog__fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );

  const footer = (
    <div
      style={{
        flex: '0 0 auto',
        padding: isDesktop ? '16px 26px' : '12px 18px 20px',
        borderTop: '1px solid var(--sk-divider)',
        background: 'var(--sk-bg-elev)',
        display: 'flex',
        flexDirection: isDesktop ? 'row' : 'column',
        gap: 10,
      }}
    >
      {error ? (
        <span style={{ fontSize: 12, color: 'var(--sk-danger)', flex: isDesktop ? 1 : undefined }}>
          {error}
        </span>
      ) : isDesktop ? (
        <span style={{ flex: 1 }} />
      ) : null}
      {isDesktop && step > 1 && step < 5 ? (
        <V2Button variant="ghost" disabled={saving} onClick={() => setStep((step - 1) as Step)}>
          بازگشت
        </V2Button>
      ) : null}
      <V2Button
        variant="primary"
        disabled={saving}
        onClick={() => (step === 5 ? finish() : advance((step + 1) as Step))}
        style={isDesktop ? undefined : { width: '100%', height: 50 }}
      >
        {saving ? 'در حال ذخیره…' : cta}
      </V2Button>
    </div>
  );

  const body = (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        padding: isDesktop ? '24px 26px' : '20px 18px',
      }}
    >
      {StepBody()}
    </div>
  );

  if (!isDesktop) {
    return (
      <div
        className="sk-app sk-safe-top"
        dir="rtl"
        data-theme={themeMode === 'dark' ? 'dark' : 'light'}
        data-brand={brandColor}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 'var(--sk-z-modal)' as unknown as number,
          background: 'var(--sk-bg-elev)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {header}
        {body}
        {footer}
      </div>
    );
  }

  return (
    <div
      className="sk-app sk-safe-top"
      dir="rtl"
      data-theme={themeMode === 'dark' ? 'dark' : 'light'}
      data-brand={brandColor}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 'var(--sk-z-modal)' as unknown as number,
        background: 'rgba(15,18,25,.48)',
        backdropFilter: 'blur(8px)',
        display: 'grid',
        placeItems: 'center',
        padding: 30,
      }}
    >
      <div
        style={{
          width: 720,
          maxWidth: '100%',
          maxHeight: '86vh',
          background: 'var(--sk-bg-elev)',
          border: '1px solid var(--sk-border)',
          borderRadius: 'var(--sk-r-xl)',
          boxShadow: 'var(--sk-shadow-4)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {header}
        {body}
        {footer}
      </div>
    </div>
  );
}
